"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Calendar, User, ChevronLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="min-h-[60vh] w-full animate-pulse bg-muted/50 flex items-center justify-center">
        <div className="h-8 w-64 bg-muted rounded-md"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-20 text-center">
        <p className="text-destructive">Failed to load post content.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto py-20 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="w-full">
      {}
      <div className="relative h-[50vh] min-h-[400px] w-full bg-primary/90 overflow-hidden">
        {post.featuredImage && (
          <img
            src={post.featuredImage}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover opacity-60 mix-blend-overlay"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 px-6 text-center">
          <div className="container max-w-4xl mx-auto">
            <div className="mb-6 flex justify-center">
               <Link to="#" className="inline-flex items-center text-white/80 hover:text-white transition-colors text-sm font-medium">
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back to Blog
               </Link>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight drop-shadow-md">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto max-w-4xl px-6 py-4">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-muted-foreground">
            {post.author && (
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8 border border-border">
                  <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground">
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-foreground">{post.author.name}</span>
              </div>
            )}
            
            {post.date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 opacity-70" />
                <span>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}

            {post.categories && post.categories.length > 0 && (
              <div className="flex gap-2">
                {post.categories.map((cat, i) => (
                  <Badge 
                    key={cat.id} 
                    data-index={i} 
                    className="bg-chart-2 text-chart-2-foreground hover:bg-chart-2/90 border-none font-medium px-3 py-1"
                  >
                    {cat.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      <div className="container mx-auto max-w-3xl px-6 py-16 md:py-24">
        <div
          className="font-sans text-foreground/90 leading-relaxed text-lg
                     [&>p]:mb-8 [&>p]:text-foreground/80
                     [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-semibold [&>h2]:mt-16 [&>h2]:mb-6 [&>h2]:text-foreground
                     [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-medium [&>h3]:mt-10 [&>h3]:mb-4 [&>h3]:text-foreground
                     [&>h4]:font-default [&>h4]:text-xl [&>h4]:font-medium [&>h4]:mt-8 [&>h4]:mb-4 [&>h4]:text-foreground
                     [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-8 [&>ul>li]:mb-2 [&>ul>li]:text-foreground/80
                     [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-8 [&>ol>li]:mb-2 [&>ol>li]:text-foreground/80
                     [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>a]:font-medium
                     [&>blockquote]:border-l-4 [&>blockquote]:border-chart-2 [&>blockquote]:bg-accent/30 [&>blockquote]:p-6 [&>blockquote]:italic [&>blockquote]:text-foreground/70 [&>blockquote]:my-10 [&>blockquote]:rounded-r-lg
                     [&>img]:rounded-2xl [&>img]:shadow-md [&>img]:my-10 [&>img]:w-full [&>img]:object-cover
                     [&>figure]:my-10 [&>figure>img]:rounded-2xl [&>figure>img]:shadow-md [&>figure>img]:w-full [&>figure>figcaption]:text-center [&>figure>figcaption]:text-sm [&>figure>figcaption]:text-muted-foreground [&>figure>figcaption]:mt-3"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />
      </div>
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-[400px] rounded-2xl bg-background/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card 
          key={post.id} 
          data-index={i} 
          className="overflow-hidden flex flex-col h-full border-border/40 shadow-sm hover:shadow-md transition-all duration-300 bg-card group"
        >
          {post.featuredImage ? (
            <AspectRatio ratio={16 / 10} className="overflow-hidden bg-muted">
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" 
              />
            </AspectRatio>
          ) : (
            <AspectRatio ratio={16 / 10} className="bg-secondary flex items-center justify-center">
              <span className="text-muted-foreground font-default">Birthing Joy</span>
            </AspectRatio>
          )}
          
          <CardHeader className="flex-grow pt-6">
            {post.categories && post.categories.length > 0 && (
              <div className="mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-chart-3">
                  {post.categories[0].name}
                </span>
              </div>
            )}
            <CardTitle className="font-default text-xl leading-tight line-clamp-2 group-hover:text-foreground transition-colors">
              {post.title}
            </CardTitle>
            {post.excerpt && (
              <CardDescription className="line-clamp-3 mt-3 font-sans text-foreground/70 text-sm leading-relaxed">
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              </CardDescription>
            )}
          </CardHeader>
          
          <CardFooter className="pb-6 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button 
                variant="outline" 
                className="w-full border-primary/20 text-primary hover:bg-accent hover:text-primary bg-secondary transition-colors"
              >
                Read Article
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" };
  
  const mainQuery = new WP_Query(currentWPQueryParams);

  
  const relatedQuery = new WP_Query({ 
    post_type: "post", 
    per_page: 3,
    
    
  });

  return (
    <section id="single-post-template-section" className="bg-background text-foreground w-full">
      {}
      <PostsProvider wp_query={mainQuery}>
        <SinglePostContent />
      </PostsProvider>

      {}
      <div className="bg-accent/40 py-20 border-t border-border/50">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="text-center mb-12">
            <h2 className="font-default text-3xl md:text-4xl text-foreground font-semibold mb-4">
              More from the Journal
            </h2>
            <p className="text-muted-foreground font-sans max-w-2xl mx-auto">
              Explore more insights, stories, and guidance for your journey through pregnancy, birth, and postpartum.
            </p>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsGrid />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}